import Layout from 'components/ui/Layout';
import { useBooking } from 'features/booking/hooks/useBooking';
import {
  ArrowLeft,
  Calendar,
  Clock,
  CreditCard,
  IndianRupee,
  MapPin,
  MessageSquare,
  Package,
  Star,
  User,
  Users,
  Download
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDateTime } from 'utils/dateFormatter';

const ViewBookingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: response, isLoading, isError, error } = useBooking(id);

  const booking = response?.data;

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      confirmed: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      in_progress: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      completed: 'bg-green-500/10 text-green-600 dark:text-green-400',
      cancelled: 'bg-red-500/10 text-red-600 dark:text-red-400',
      rescheduled: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
      rejected: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      processing: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      completed: 'bg-green-500/10 text-green-600 dark:text-green-400',
      failed: 'bg-red-500/10 text-red-600 dark:text-red-400',
      refunded: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      partially_refunded:
        'bg-orange-500/10 text-orange-600 dark:text-orange-400',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  if (isLoading) {
    return (
      <Layout>
        <div className='flex items-center justify-center h-screen'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
        </div>
      </Layout>
    );
  }

  if (isError) {
    return (
      <Layout>
        <div className='flex flex-col items-center justify-center h-screen'>
          <div className='text-destructive text-lg mb-4'>
            Error loading booking
          </div>
          <p className='text-muted-foreground mb-4'>
            {error?.message || 'Something went wrong'}
          </p>
          <button
            onClick={() => navigate('/bookings')}
            className='px-4 py-2 bg-primary text-primary-foreground rounded-md'
          >
            Back to Bookings
          </button>
        </div>
      </Layout>
    );
  }

  if (!booking) {
    return (
      <Layout>
        <div className='flex flex-col items-center justify-center h-screen'>
          <div className='text-lg mb-4'>Booking not found</div>
          <button
            onClick={() => navigate('/bookings')}
            className='px-4 py-2 bg-primary text-primary-foreground rounded-md'
          >
            Back to Bookings
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='min-h-screen bg-background'>
        <div className='bg-card border-b border-border sticky top-0 z-10'>
          <div className='container mx-auto px-4 py-4'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-4'>
                <button
                  onClick={() => navigate('/bookings')}
                  className='p-2 hover:bg-muted rounded-md transition-colors'
                  title='Back to bookings'
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <h1 className='text-2xl font-bold flex items-center gap-2'>
                    <Calendar className='text-primary' size={24} />
                    Booking Details
                  </h1>
                  <p className='text-sm text-muted-foreground'>
                    #{booking.bookingNumber}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='container mx-auto px-4 py-6'>
          <div className='bg-card border border-border rounded-lg p-6 space-y-6'>
            {/* Status Badges */}
            <div className='flex flex-wrap gap-2'>
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(booking.status)}`}
              >
                {booking.status?.replace(/_/g, ' ')?.toUpperCase()}
              </span>
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${getPaymentStatusColor(
                  booking.payment?.status,
                )}`}
              >
                PAYMENT:{' '}
                {booking.payment?.status?.replace(/_/g, ' ')?.toUpperCase()}
              </span>
              <span className='px-3 py-1 text-xs font-medium rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400'>
                {booking.bookingType?.replace(/_/g, ' ')?.toUpperCase()}
              </span>
            </div>

            {/* Customer and Seller Information */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='space-y-2 bg-muted rounded-lg p-4'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <User size={14} />
                  <span>Customer Information</span>
                </label>
                <div className='flex items-center space-x-3'>
                  {booking.customerId?.profilePhoto ? (
                    <img
                      src={booking.customerId.profilePhoto}
                      alt='Customer'
                      className='w-12 h-12 rounded-full object-cover border border-border'
                    />
                  ) : (
                    <div className='w-12 h-12 rounded-full bg-primary/10 border border-border flex items-center justify-center'>
                      <User size={24} />
                    </div>
                  )}
                  <div>
                    <p className='text-sm font-medium'>
                      {booking.customerId?.firstName || 'N/A'} {booking.customerId?.lastName || 'N/A'}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      Phone No: {booking.customerId?.phoneNumber}
                    </p>
                  </div>
                </div>
              </div>

              <div className='space-y-2 bg-muted rounded-lg p-4'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <User size={14} />
                  <span>Seller Information</span>
                </label>
                <div className='flex items-center space-x-3'>
                  {booking.sellerId?.profilePhoto ? (
                    <img
                      src={booking.sellerId.profilePhoto}
                      alt='Seller'
                      className='w-12 h-12 rounded-full object-cover border border-border'
                    />
                  ) : (
                    <div className='w-12 h-12 rounded-full bg-purple-500/10 border border-border flex items-center justify-center'>
                      <User size={24} />
                    </div>
                  )}
                  <div>
                    <p className='text-sm font-medium'>
                      {booking.sellerId?.firstName || 'N/A'} {booking.sellerId?.lastName || 'N/A'}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      Phone No: {booking.sellerId?.phoneNumber || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Information */}
            {/* <div className='space-y-2 bg-muted rounded-lg p-4'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <Package size={14} />
                <span>Service Details</span>
              </label>
              <div className='space-y-2'>
                <p className='text-sm font-medium'>
                  {booking.serviceId?.name || 'N/A'}
                </p>
                {booking.serviceId?.description && (
                  <p className='text-xs text-muted-foreground'>
                    {booking.serviceId.description}
                  </p>
                )}
                {booking.serviceId?.category && (
                  <span className='inline-flex px-2 py-1 bg-primary/10 text-primary text-xs rounded'>
                    {booking.serviceId.category}
                  </span>
                )}
              </div>
            </div> */}

        

            {/* Location */}
            {booking.serviceLocation && (
              <div className='space-y-2 bg-muted rounded-lg p-4'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <MapPin size={14} />
                  <span>Location</span>
                </label>
                <div className='space-y-2'>
                  {booking.serviceLocation && (
                    <p className='text-xs text-muted-foreground'>
                      {booking.serviceLocation.addressLine1} {booking.serviceLocation.addressLine2}
                    </p>
                  )}
                  {booking.serviceLocation.address && (
                    <p className='text-sm'>{booking.serviceLocation.address}</p>
                  )}
                  {booking.serviceLocation.coordinates?.coordinates && (
                    <>
                      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                        <span>Coordinates:</span>
                        <span>
                          Lat:{' '}
                          {booking.serviceLocation.coordinates.coordinates[1]?.toFixed(
                            6,
                          )}
                          , Long:{' '}
                          {booking.serviceLocation.coordinates.coordinates[0]?.toFixed(
                            6,
                          )}
                        </span>
                      </div>
                      <a
                        href={`https://www.google.com/maps?q=${booking.serviceLocation.coordinates.coordinates[1]},${booking.serviceLocation.coordinates.coordinates[0]}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center gap-1 text-sm text-primary hover:underline'
                      >
                        <MapPin size={14} />
                        Open in Google Maps
                      </a>
                    </>
                  )}
                </div>
              </div>
            )}

            {booking.serviceItems?.length > 0 ? (
              <div className='space-y-3 bg-muted rounded-lg p-4'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center justify-between'>
                  <span className='flex items-center space-x-1'>
                    <Package size={14} />
                    <span>
                      Service Details (
                      {booking.serviceItems.reduce(
                        (sum, it) => sum + (it.quantity || 1),
                        0
                      )}{' '}
                      items)
                    </span>
                  </span>
                </label>
                <div className='divide-y divide-border/60'>
                  {booking.serviceItems.map((item, idx) => {
                    const unitP =
                      item.discountedPrice > 0
                        ? item.discountedPrice
                        : item.basePrice;
                    const qty = item.quantity || 1;
                    const itemTotal =
                      item.itemTotal != null ? item.itemTotal : unitP * qty;
                    return (
                      <div
                        key={item.catalogEntryId || item.serviceId || idx}
                        className='py-2 flex items-center justify-between'
                      >
                        <div className='flex items-center gap-3'>
                          {item.imageUrl && (
                            <img
                              src={item.imageUrl}
                              alt={item.name}
                              className='w-10 h-10 rounded-md object-cover border'
                            />
                          )}
                          <div>
                            <p className='text-sm font-medium'>
                              {item.name}{' '}
                              {qty > 1 && (
                                <span className='text-primary font-bold ml-1'>
                                  (x{qty})
                                </span>
                              )}
                            </p>
                            {item.categoryId?.name?.english && (
                              <span className='text-xs text-muted-foreground'>
                                {item.categoryId.name.english}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className='text-sm font-semibold'>
                          ₹{itemTotal != null ? Number(itemTotal).toFixed(2) : 'N/A'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : booking.serviceId ? (
              <div className='space-y-2 bg-muted rounded-lg p-4'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <Package size={14} />
                  <span>Service Details</span>
                </label>

                <div className='space-y-2'>
                  <p className='text-sm font-medium'>
                    {booking.serviceId?.name || 'N/A'}
                  </p>

                  {booking.serviceId?.description && (
                    <p className='text-xs text-muted-foreground'>
                      {booking.serviceId.description}
                    </p>
                  )}

                  {booking.serviceId?.category && (
                    <span className='inline-flex px-2 py-1 bg-primary/10 text-primary text-xs rounded'>
                      {booking.serviceId.category}
                    </span>
                  )}
                </div>
              </div>
            ) : booking?.foodItems?.length > 0 ? (
  <div className='space-y-2 bg-muted rounded-lg p-4'>
    <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
      <Package size={14} />
      <span>Order Details</span>
    </label>
<div className='space-y-2'>
  <div>
    <span className='text-sm font-medium'>Item Name: </span>
    <span className='text-sm text-muted-foreground'>
      {booking?.foodItems?.length
        ? booking.foodItems.map((item) => item.name).join(', ')
        : 'N/A'}
    </span>
  </div>

  <div>
    <span className='text-sm font-medium'>Food Type: </span>
    <span className='text-sm text-muted-foreground'>
      {booking?.foodItems?.length
        ? booking.foodItems.map((item) => item.foodType).join(', ')
        : 'N/A'}
    </span>
  </div>
</div>
  </div>
) : null}
            
            {/* Pricing Details */}
            <div className='space-y-2 bg-muted rounded-lg p-4'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <IndianRupee size={14} />
                <span>Pricing Details</span>
              </label>
              <div className='space-y-2'>
                <div className='flex justify-between text-sm'>
                  <span className='text-muted-foreground'>Base Amount:</span>
                  <span className='font-medium'>
                    {booking.pricing?.currency || 'INR'}{' '}
                    {booking.pricing?.baseAmount?.toFixed(2)}
                  </span>
                </div>
                {booking.pricing?.couponCode && (
                  <div className='flex justify-between text-sm'>
                    <span className='text-muted-foreground'>
                      Coupon ({booking.pricing.couponCode}):
                    </span>
                    <span className='font-medium text-green-600'>
                      -{booking.pricing?.currency || 'INR'}{' '}
                      {booking.pricing.couponDiscount?.toFixed(2)}
                    </span>
                  </div>
                )}
                {booking.pricing?.discountAmount > 0 && (
                  <div className='flex justify-between text-sm'>
                    <span className='text-muted-foreground'>Discount:</span>
                    <span className='font-medium text-green-600'>
                      -{booking.pricing?.currency || 'INR'}{' '}
                      {booking.pricing.discountAmount?.toFixed(2)}
                    </span>
                  </div>
                )}
                {booking.pricing?.taxAmount > 0 && (
                  <div className='flex justify-between text-sm'>
                    <span className='text-muted-foreground'>Tax:</span>
                    <span className='font-medium'>
                      {booking.pricing?.currency || 'INR'}{' '}
                      {booking.pricing.taxAmount?.toFixed(2)}
                    </span>
                  </div>
                )}
                {booking.pricing?.platformFee > 0 && (
                  <div className='flex justify-between text-sm'>
                    <span className='text-muted-foreground'>Platform Fee:</span>
                    <span className='font-medium'>
                      {booking.pricing?.currency || 'INR'}{' '}
                      {booking.pricing.platformFee?.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className='flex justify-between text-base font-bold pt-2 border-t border-border'>
                  <span>Total Amount:</span>
                  <span className='text-primary'>
                    {booking.pricing?.currency || 'INR'}{' '}
                    {booking.pricing?.totalAmount?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className='space-y-2 bg-muted rounded-lg p-4'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <CreditCard size={14} />
                <span>Payment Information</span>
              </label>
              <div className='grid grid-cols-2 md:grid-cols-3 gap-3 text-sm'>
                <div>
                  <span className='text-muted-foreground text-xs'>
                    Payment Mode:
                  </span>
                  <p className='font-medium'>
                    {booking.payment?.paymentMode?.toUpperCase() || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className='text-muted-foreground text-xs'>
                    Payment Status:
                  </span>
                  <p className='font-medium'>
                    {booking.payment?.paymentStatus?.toUpperCase() || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className='text-muted-foreground text-xs'>
                    Transaction ID:
                  </span>
                  <p className='font-medium text-xs'>
                    {booking.payment?.escrowTransactionId || 'N/A'}
                  </p>
                </div>

                <div>
                  <span className='text-muted-foreground text-xs'>
                    Seller earnings:
                  </span>
                  <p className='font-medium text-xs'>
                   INR {booking?.commission?.sellerEarnings || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Assigned Staff */}
            {booking.assignedStaffId && (
              <div className='space-y-2 bg-muted rounded-lg p-4'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <Users size={14} />
                  <span>Assigned Staff</span>
                </label>
                <div className='space-y-2'>
                  <div className='flex items-center space-x-3 p-2 bg-background rounded'>
                    <div className='w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center'>
                      <User size={16} />
                    </div>
                    <div>
                      <p className='text-sm font-medium'>
                        {booking.assignedStaffId?.phoneNumber || 'N/A'}
                      </p>
                      <p className='text-xs text-muted-foreground'>
                        {booking.assignedStaffId?.email || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Staff Assignment History */}
            {booking.staffAssignmentHistory &&
              booking.staffAssignmentHistory.length > 0 && (
                <div className='space-y-2 bg-muted rounded-lg p-4'>
                  <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                    <Users size={14} />
                    <span>Staff Assignment History</span>
                  </label>
                  <div className='space-y-2'>
                    {booking.staffAssignmentHistory.map((assignment, index) => (
                      <div
                        key={index}
                        className='flex items-center justify-between p-2 bg-background rounded'
                      >
                        <div className='flex items-center space-x-3'>
                          <div className='w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center'>
                            <User size={16} />
                          </div>
                          <div>
                            <p className='text-sm font-medium'>
                              Staff ID: {assignment.staffId}
                            </p>
                            <p className='text-xs text-muted-foreground'>
                              Status: {assignment.status}
                            </p>
                          </div>
                        </div>
                        <span className='text-xs text-muted-foreground'>
                          {formatDateTime(assignment.assignedAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Schedule Information */}
            {(booking.scheduledDate || booking.scheduledTime) && (
              <div className='space-y-2 bg-muted rounded-lg p-4'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <Calendar size={14} />
                  <span>Schedule</span>
                </label>
                <div className='space-y-2'>
                  {booking.scheduledDate && (
                    <div className='flex items-center gap-2 text-sm'>
                      <span className='text-muted-foreground'>
                        Scheduled Date:
                      </span>
                      <span className='font-medium'>
                        {formatDateTime(booking.scheduledDate)}
                      </span>
                    </div>
                  )}
                  {booking.scheduledTime && (
                    <div className='flex items-center gap-2 text-sm'>
                      <span className='text-muted-foreground'>
                        Scheduled Time:
                      </span>
                      <span className='font-medium'>
                        {booking.scheduledTime}
                      </span>
                    </div>
                  )}
                  {/* {booking.duration && (
                    <div className='flex items-center gap-2 text-sm'>
                      <span className='text-muted-foreground'>Duration:</span>
                      <span className='font-medium'>
                        {booking.duration} minutes
                      </span>
                    </div>
                  )}
                  {booking.estimatedEndTime && (
                    <div className='flex items-center gap-2 text-sm'>
                      <span className='text-muted-foreground'>
                        Estimated End Time:
                      </span>
                      <span className='font-medium'>
                        {formatDateTime(booking.estimatedEndTime)}
                      </span>
                    </div>
                  )} */}
                </div>
              </div>
            )}

            {/* Rating */}
            {booking.rating &&
              (booking.rating.customer || booking.rating.seller) && (
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  {booking.rating.customer && (
                    <div className='space-y-2 bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-4'>
                      <label className='text-xs font-medium text-yellow-600 dark:text-yellow-400 uppercase flex items-center space-x-1'>
                        {/* <Star size={14} /> */}
                        <span>Customer Rating</span>
                      </label>
                      <div className='space-y-2'>
                        <div className='flex items-center gap-2'>
                          <div className='flex'>
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                size={16}
                                className={
                                  i < booking.rating.customer.rating
                                    ? 'fill-yellow-500 text-yellow-500'
                                    : 'text-gray-300'
                                }
                              />
                            ))}
                          </div>
                          <span className='text-sm font-medium'>
                            {booking.rating.customer.rating}/5
                          </span>
                        </div>
                        {booking.rating.customer.review && (
                          <p className='text-sm text-muted-foreground'>
                            {booking.rating.customer.review}
                          </p>
                        )}
                        {booking.rating.customer.ratedAt && (
                          <p className='text-xs text-muted-foreground'>
                            Rated at:{' '}
                            {formatDateTime(booking.rating.customer.ratedAt)}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {booking.rating.seller && (
                    <div className='space-y-2 bg-purple-500/5 border border-purple-500/20 rounded-lg p-4'>
                      <label className='text-xs font-medium text-purple-600 dark:text-purple-400 uppercase flex items-center space-x-1'>
                        {/* <Star size={14} /> */}
                        <span>Seller Rating</span>
                      </label>
                      <div className='space-y-2'>
                        <div className='flex items-center gap-2'>
                          <div className='flex'>
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                size={16}
                                className={
                                  i < booking.rating.seller.rating
                                    ? 'fill-purple-500 text-purple-500'
                                    : 'text-gray-300'
                                }
                              />
                            ))}
                          </div>
                          <span className='text-sm font-medium'>
                            {booking.rating.seller.rating}/5
                          </span>
                        </div>
                        {booking.rating.seller.review && (
                          <p className='text-sm text-muted-foreground'>
                            {booking.rating.seller.review}
                          </p>
                        )}
                        {booking.rating.seller.ratedAt && (
                          <p className='text-xs text-muted-foreground'>
                            Rated at:{' '}
                            {formatDateTime(booking.rating.seller.ratedAt)}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* Notes */}
            {(booking.customerNotes ||
              booking.sellerNotes ||
              booking.adminNotes) && (
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                {booking.customerNotes && (
                  <div className='space-y-2 bg-blue-500/5 border border-blue-500/20 rounded-lg p-4'>
                    <label className='text-xs font-medium text-blue-600 dark:text-blue-400 uppercase flex items-center space-x-1'>
                      <MessageSquare size={14} />
                      <span>Customer Notes</span>
                    </label>
                    <p className='text-sm'>{booking.customerNotes}</p>
                  </div>
                )}
                {booking.sellerNotes && (
                  <div className='space-y-2 bg-purple-500/5 border border-purple-500/20 rounded-lg p-4'>
                    <label className='text-xs font-medium text-purple-600 dark:text-purple-400 uppercase flex items-center space-x-1'>
                      <MessageSquare size={14} />
                      <span>Seller Notes</span>
                    </label>
                    <p className='text-sm'>{booking.sellerNotes}</p>
                  </div>
                )}
                {booking.adminNotes && (
                  <div className='space-y-2 bg-gray-500/5 border border-gray-500/20 rounded-lg p-4'>
                    <label className='text-xs font-medium text-gray-600 dark:text-gray-400 uppercase flex items-center space-x-1'>
                      <MessageSquare size={14} />
                      <span>Admin Notes</span>
                    </label>
                    <p className='text-sm'>{booking.adminNotes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Cancellation Info */}
            {booking.cancellation && (
              <div className='space-y-2 bg-red-500/5 border border-red-500/20 rounded-lg p-4'>
                <label className='text-xs font-medium text-red-600 dark:text-red-400 uppercase'>
                  Cancellation Details
                </label>
                <div className='space-y-2 text-sm'>
                 <div>
  <span className='text-muted-foreground'>Cancelled By:</span>

  <div className='mt-1'>
    <p className='font-medium'>
      {booking.cancellation.cancelledBy?.firstName}{' '}
      {booking.cancellation.cancelledBy?.lastName}
    </p>

    <p className='text-xs text-muted-foreground'>
      {booking.cancellation.cancelledBy?.countryCode}{' '}
      {booking.cancellation.cancelledBy?.phoneNumber}
    </p>

    <p className='text-xs text-muted-foreground'>
      {booking.cancellation.cancelledBy?.email}
    </p>
  </div>
</div>
                  <div>
                    <span className='text-muted-foreground'>Reason:</span>
                    <p className='mt-1'>
                      {booking.cancellation.cancellationReason || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className='text-muted-foreground'>Cancelled At:</span>
                    <span className='ml-2 font-medium'>
                      {formatDateTime(booking.cancellation.cancelledAt)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Rejection Info */}
            {booking.rejection && (
              <div className='space-y-2 bg-gray-500/5 border border-gray-500/20 rounded-lg p-4'>
                <label className='text-xs font-medium text-gray-600 dark:text-gray-400 uppercase'>
                  Rejection Details
                </label>
                <div className='space-y-2 text-sm'>
                  {booking.rejection.rejectedBy && (
                    <div>
                      <span className='text-muted-foreground'>
                        Rejected By:
                      </span>
                      <div className='mt-1'>
                        <p className='font-medium'>
                          {booking.rejection.rejectedBy.name}
                        </p>
                        <p className='text-xs'>
                          {booking.rejection.rejectedBy.email}
                        </p>
                        <p className='text-xs'>
                          {booking.rejection.rejectedBy.phone}
                        </p>
                      </div>
                    </div>
                  )}
                  <div>
                    <span className='text-muted-foreground'>Reason:</span>
                    <p className='mt-1'>
                      {booking.rejection.rejectionReason || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className='text-muted-foreground'>Rejected At:</span>
                    <span className='ml-2 font-medium'>
                      {formatDateTime(booking.rejection.rejectedAt)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Reschedule History */}
            {booking.rescheduleHistory &&
              booking.rescheduleHistory.length > 0 && (
                <div className='space-y-2 bg-orange-500/5 border border-orange-500/20 rounded-lg p-4'>
                  <label className='text-xs font-medium text-orange-600 dark:text-orange-400 uppercase'>
                    Reschedule History
                  </label>
                  <div className='space-y-3'>
                    {booking.rescheduleHistory.map((reschedule, index) => (
                      <div
                        key={index}
                        className='p-3 bg-background rounded border border-orange-200 dark:border-orange-800'
                      >
                        <div className='space-y-2 text-sm'>
                          <div>
                            <span className='text-muted-foreground'>
                              Requested By:
                            </span>
                            <span className='ml-2 font-medium'>
                              {reschedule.requestedBy} (ID:{' '}
                              {reschedule.requestedById})
                            </span>
                          </div>
                          <div>
                            <span className='text-muted-foreground'>
                              Old Date:
                            </span>
                            <span className='ml-2 font-medium'>
                              {formatDateTime(reschedule.oldScheduledDate)}
                            </span>
                          </div>
                          <div>
                            <span className='text-muted-foreground'>
                              New Date:
                            </span>
                            <span className='ml-2 font-medium'>
                              {formatDateTime(reschedule.newScheduledDate)}
                            </span>
                          </div>
                          <div>
                            <span className='text-muted-foreground'>
                              Status:
                            </span>
                            <span className='ml-2 font-medium'>
                              {reschedule.status}
                            </span>
                          </div>
                          <div>
                            <span className='text-muted-foreground'>
                              Requested At:
                            </span>
                            <span className='ml-2 font-medium'>
                              {formatDateTime(reschedule.requestedAt)}
                            </span>
                          </div>
                          {reschedule.respondedAt && (
                            <div>
                              <span className='text-muted-foreground'>
                                Responded At:
                              </span>
                              <span className='ml-2 font-medium'>
                                {formatDateTime(reschedule.respondedAt)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Invoices */}
{(booking.invoiceUrl || booking.sellerInvoiceUrl) && (
  <div className='space-y-2 bg-muted rounded-lg p-4'>
    <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
      <CreditCard size={14} />
      <span>Invoices</span>
    </label>

    <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
      {booking.invoiceUrl && (
        <a
          href={booking.invoiceUrl}
          target='_blank'
          rel='noopener noreferrer'
          className='flex items-center justify-between p-3 bg-background rounded border border-border hover:border-primary transition-colors'
        >
          <div>
            <p className='text-sm font-medium'>Customer Invoice</p>
            <p className='text-xs text-muted-foreground'>
              View / Download PDF
            </p>
          </div>

          <Download
            size={18}
            className='text-primary'
          />
        </a>
      )}

      {booking.sellerInvoiceUrl && (
        <a
          href={booking.sellerInvoiceUrl}
          target='_blank'
          rel='noopener noreferrer'
          className='flex items-center justify-between p-3 bg-background rounded border border-border hover:border-primary transition-colors'
        >
          <div>
            <p className='text-sm font-medium'>Seller Statement</p>
            <p className='text-xs text-muted-foreground'>
              View / Download PDF
            </p>
          </div>

          <Download
            size={18}
            className='text-primary'
          />
        </a>
      )}
    </div>
  </div>
)}

            {/* Timestamps */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border'>
              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <Clock size={14} />
                  <span>Created At</span>
                </label>
                <p className='text-sm'>{formatDateTime(booking.createdAt)}</p>
              </div>

              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <Clock size={14} />
                  <span>Last Updated</span>
                </label>
                <p className='text-sm'>{formatDateTime(booking.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ViewBookingPage;
